import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, User, Palette, Shirt, Sparkles, Shuffle, Eye, Smile, Crown, PersonStanding } from 'lucide-react';
import { avatarAPI } from '../api';
import { toast } from 'sonner';

// ============ FULL BODY AVATAR PREVIEW ============
// Renders a complete character with body, clothing, hair, and accessories
export const FullBodyAvatarPreview = ({ 
  avatar, 
  size = 'md',
  showBadge = false,
  type = 'explorer',
  showAura = false,
  auraColor = '#818CF8',
  showBreathing = false,
  showSparkles = false,
  isIdle = false,
  equippedEffects = [] // Array of effect IDs like ['effect_star_glow', 'effect_sparkle_trail']
}) => {
  // Size configurations for the full body
  const sizes = {
    sm: { width: 48, height: 72, scale: 0.5 },
    md: { width: 80, height: 120, scale: 0.75 },
    lg: { width: 120, height: 180, scale: 1 },
    xl: { width: 160, height: 240, scale: 1.25 }
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  const { width, height, scale } = sizeConfig;
  
  // Enable idle animations
  const enableBreathing = showBreathing || isIdle;
  
  // Check for equipped Temple Effects
  const hasStarGlow = equippedEffects.includes('effect_star_glow');
  const hasSparkleTrail = equippedEffects.includes('effect_sparkle_trail');
  const effectiveShowAura = showAura || hasStarGlow;
  const effectiveAuraColor = hasStarGlow ? '#FBBF24' : auraColor; // Gold for star glow
  const effectiveShowSparkles = showSparkles || hasSparkleTrail;
  
  // Extract avatar properties with defaults
  const skinColor = avatar?.skin_tone || '#F5D0C5';
  const hairColor = avatar?.hair_color || '#2C1810';
  const hairStyle = avatar?.hair_style || 'medium';
  const eyeColor = avatar?.eye_color || '#4A3728';
  const eyeShape = avatar?.eye_shape || 'round';
  const smileType = avatar?.smile || 'happy';
  const faceFeature = avatar?.face_feature || 'none';
  const outfit = avatar?.outfit || 'explorer_tunic';
  const accessory = avatar?.accessory || 'none';
  
  // Outfit colors
  const outfitColors = {
    explorer_tunic: '#818CF8',
    star_robe: '#FBBF24',
    forest_cloak: '#22C55E',
    ocean_vest: '#06B6D4',
    fire_jacket: '#EF4444',
    sky_cape: '#8B5CF6',
    moon_robes: '#94A3B8',
    sun_garment: '#F59E0B',
    crystal_armor: '#67E8F9',
    hoodie: '#64748B',
    cozy_sweater: '#FB7185',
    t_shirt: '#38BDF8',
    explorer_jacket: '#A16207',
    adventurer_vest: '#65A30D',
    ranger_cloak: '#166534',
    royal_cape: '#7C3AED',
    wizard_robe: '#4338CA',
    enchanted_dress: '#EC4899',
    superhero_suit: '#DC2626',
    space_suit: '#3730A3',
    ninja_outfit: '#1F2937',
    pirate_vest: '#92400E',
    // Store unlockable outfits
    starlight_robe: '#E0E7FF',
    forest_guardian: '#166534',
    ocean_explorer: '#0891B2'
  };
  
  const outfitColor = outfitColors[outfit] || '#818CF8';
  const outfitColorDark = adjustColor(outfitColor, -30);
  const outfitColorLight = adjustColor(outfitColor, 30);
  
  // Helper to adjust color brightness
  function adjustColor(hex, amount) {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
  
  // Get accessory emoji
  const getAccessoryEmoji = (acc) => {
    const accessoryEmojis = {
      none: '',
      glasses: '👓', round_glasses: '🤓', sport_glasses: '🕶️', goggles: '🥽',
      crown: '👑', tiara: '👸', wizard_hat: '🧙', beanie: '🧢', cap: '🧢',
      headband: '🎗️', cat_ears: '🐱', bunny_ears: '🐰', antenna: '📡',
      earrings: '💎', necklace: '📿', pendant: '💫', scarf: '🧣',
      hairclips: '🎀', flower: '🌸', feather: '🪶', bow: '🎀',
      headphones: '🎧', wings: '🦋', halo: '😇', backpack: '🎒',
      star_crown: '⭐', magic_goggles: '✨', butterfly_clips: '🦋',
      dragon_horns: '🐉', crystal_earrings: '💠'
    };
    return accessoryEmojis[acc] || '';
  };
  
  // ============ RENDER OUTFIT (with special store-unlockable designs) ============
  const renderOutfit = () => {
    const torsoY = height - 70 * scale;
    
    // Special outfits with unique designs
    if (outfit === 'starlight_robe') {
      // Flowing white/silver robe with star patterns
      return (
        <>
          <defs>
            <linearGradient id="starlightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E0E7FF" />
              <stop offset="50%" stopColor="#C7D2FE" />
              <stop offset="100%" stopColor="#A5B4FC" />
            </linearGradient>
          </defs>
          {/* Main robe body - flowing */}
          <ellipse cx={width/2} cy={torsoY} rx={28 * scale} ry={38 * scale} fill="url(#starlightGradient)" />
          {/* Inner robe layer */}
          <ellipse cx={width/2} cy={torsoY} rx={20 * scale} ry={28 * scale} fill="#EEF2FF" opacity={0.5} />
          {/* Star decorations */}
          <circle cx={width/2 - 10 * scale} cy={torsoY - 10 * scale} r={2 * scale} fill="#FBBF24" opacity={0.8} />
          <circle cx={width/2 + 8 * scale} cy={torsoY + 5 * scale} r={1.5 * scale} fill="#FCD34D" opacity={0.7} />
          <circle cx={width/2 - 5 * scale} cy={torsoY + 15 * scale} r={1 * scale} fill="#FBBF24" opacity={0.6} />
          <circle cx={width/2 + 12 * scale} cy={torsoY - 5 * scale} r={1.5 * scale} fill="#FDE68A" opacity={0.7} />
          {/* Flowing sleeves */}
          <ellipse cx={width/2 - 35 * scale} cy={torsoY - 5 * scale} rx={10 * scale} ry={25 * scale} fill="url(#starlightGradient)" />
          <ellipse cx={width/2 + 35 * scale} cy={torsoY - 5 * scale} rx={10 * scale} ry={25 * scale} fill="url(#starlightGradient)" />
        </>
      );
    }
    
    if (outfit === 'forest_guardian') {
      // Nature cloak with leaf patterns
      return (
        <>
          <defs>
            <linearGradient id="forestGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#166534" />
              <stop offset="50%" stopColor="#15803D" />
              <stop offset="100%" stopColor="#14532D" />
            </linearGradient>
          </defs>
          {/* Main cloak */}
          <ellipse cx={width/2} cy={torsoY} rx={28 * scale} ry={36 * scale} fill="url(#forestGradient)" />
          {/* Leaf pattern details */}
          <path 
            d={`M ${width/2 - 12 * scale} ${torsoY - 8 * scale} 
                Q ${width/2 - 8 * scale} ${torsoY - 15 * scale} ${width/2 - 4 * scale} ${torsoY - 8 * scale}`}
            fill="#22C55E" opacity={0.7}
          />
          <path 
            d={`M ${width/2 + 4 * scale} ${torsoY + 5 * scale} 
                Q ${width/2 + 10 * scale} ${torsoY - 2 * scale} ${width/2 + 14 * scale} ${torsoY + 5 * scale}`}
            fill="#4ADE80" opacity={0.6}
          />
          {/* Collar/hood hint */}
          <ellipse cx={width/2} cy={torsoY - 30 * scale} rx={12 * scale} ry={6 * scale} fill="#14532D" />
          {/* Arm cloaks */}
          <ellipse cx={width/2 - 34 * scale} cy={torsoY - 5 * scale} rx={10 * scale} ry={24 * scale} fill="url(#forestGradient)" />
          <ellipse cx={width/2 + 34 * scale} cy={torsoY - 5 * scale} rx={10 * scale} ry={24 * scale} fill="url(#forestGradient)" />
        </>
      );
    }
    
    if (outfit === 'ocean_explorer') {
      // Waterproof vest with wave patterns
      return (
        <>
          <defs>
            <linearGradient id="oceanVestGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0891B2" />
              <stop offset="50%" stopColor="#0E7490" />
              <stop offset="100%" stopColor="#155E75" />
            </linearGradient>
          </defs>
          {/* Main vest body */}
          <ellipse cx={width/2} cy={torsoY} rx={25 * scale} ry={34 * scale} fill="url(#oceanVestGradient)" />
          {/* Vest opening - shows inner shirt */}
          <ellipse cx={width/2} cy={torsoY - 10 * scale} rx={8 * scale} ry={15 * scale} fill="#F1F5F9" opacity={0.8} />
          {/* Wave pattern */}
          <path 
            d={`M ${width/2 - 15 * scale} ${torsoY + 10 * scale} 
                Q ${width/2 - 8 * scale} ${torsoY + 5 * scale} ${width/2} ${torsoY + 10 * scale}
                Q ${width/2 + 8 * scale} ${torsoY + 15 * scale} ${width/2 + 15 * scale} ${torsoY + 10 * scale}`}
            fill="#22D3EE" opacity={0.4}
          />
          {/* Pockets */}
          <rect x={width/2 - 18 * scale} y={torsoY + 5 * scale} width={8 * scale} height={10 * scale} rx={2 * scale} fill="#164E63" />
          <rect x={width/2 + 10 * scale} y={torsoY + 5 * scale} width={8 * scale} height={10 * scale} rx={2 * scale} fill="#164E63" />
          {/* Short sleeves */}
          <ellipse cx={width/2 - 32 * scale} cy={torsoY - 5 * scale} rx={8 * scale} ry={18 * scale} fill="url(#oceanVestGradient)" />
          <ellipse cx={width/2 + 32 * scale} cy={torsoY - 5 * scale} rx={8 * scale} ry={18 * scale} fill="url(#oceanVestGradient)" />
        </>
      );
    }
    
    // Default outfit rendering (for non-special outfits)
    return (
      <>
        {/* Torso / Main outfit */}
        <ellipse cx={width/2} cy={torsoY} rx={26 * scale} ry={35 * scale} fill={outfitColor} />
        {/* Outfit detail stripe */}
        <ellipse cx={width/2} cy={torsoY + 2 * scale} rx={18 * scale} ry={25 * scale} fill={outfitColorLight} opacity={0.3} />
        {/* Arms */}
        <ellipse cx={width/2 - 32 * scale} cy={torsoY - 5 * scale} rx={8 * scale} ry={22 * scale} fill={outfitColor} />
        <ellipse cx={width/2 + 32 * scale} cy={torsoY - 5 * scale} rx={8 * scale} ry={22 * scale} fill={outfitColor} />
      </>
    );
  };
  
  // Render hair based on style
  const renderHair = () => {
    const hairBaseY = 8 * scale;
    const hairWidth = 44 * scale;
    
    // Common hair styles rendering
    const hairStyles = {
      short: (
        <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={22 * scale} ry={12 * scale} fill={hairColor} />
      ),
      medium: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill={hairColor} />
          <ellipse cx={width/2 - 18 * scale} cy={hairBaseY + 20 * scale} rx={8 * scale} ry={15 * scale} fill={hairColor} />
          <ellipse cx={width/2 + 18 * scale} cy={hairBaseY + 20 * scale} rx={8 * scale} ry={15 * scale} fill={hairColor} />
        </>
      ),
      long: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill={hairColor} />
          <ellipse cx={width/2 - 20 * scale} cy={hairBaseY + 30 * scale} rx={10 * scale} ry={25 * scale} fill={hairColor} />
          <ellipse cx={width/2 + 20 * scale} cy={hairBaseY + 30 * scale} rx={10 * scale} ry={25 * scale} fill={hairColor} />
        </>
      ),
      curly: (
        <>
          <circle cx={width/2 - 15 * scale} cy={hairBaseY - 2 * scale} r={12 * scale} fill={hairColor} />
          <circle cx={width/2 + 15 * scale} cy={hairBaseY - 2 * scale} r={12 * scale} fill={hairColor} />
          <circle cx={width/2} cy={hairBaseY - 5 * scale} r={14 * scale} fill={hairColor} />
          <circle cx={width/2 - 22 * scale} cy={hairBaseY + 12 * scale} r={10 * scale} fill={hairColor} />
          <circle cx={width/2 + 22 * scale} cy={hairBaseY + 12 * scale} r={10 * scale} fill={hairColor} />
        </>
      ),
      afro: (
        <>
          <circle cx={width/2} cy={hairBaseY - 5 * scale} r={28 * scale} fill={hairColor} />
          <circle cx={width/2 - 20 * scale} cy={hairBaseY + 5 * scale} r={15 * scale} fill={hairColor} />
          <circle cx={width/2 + 20 * scale} cy={hairBaseY + 5 * scale} r={15 * scale} fill={hairColor} />
        </>
      ),
      ponytail: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill={hairColor} />
          <ellipse cx={width/2 + 22 * scale} cy={hairBaseY + 10 * scale} rx={8 * scale} ry={20 * scale} fill={hairColor} />
        </>
      ),
      buns: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={12 * scale} fill={hairColor} />
          <circle cx={width/2 - 18 * scale} cy={hairBaseY - 8 * scale} r={12 * scale} fill={hairColor} />
          <circle cx={width/2 + 18 * scale} cy={hairBaseY - 8 * scale} r={12 * scale} fill={hairColor} />
        </>
      ),
      spiky: (
        <>
          <polygon points={`${width/2 - 10 * scale},${hairBaseY + 10 * scale} ${width/2 - 5 * scale},${hairBaseY - 15 * scale} ${width/2},${hairBaseY + 10 * scale}`} fill={hairColor} />
          <polygon points={`${width/2},${hairBaseY + 10 * scale} ${width/2 + 5 * scale},${hairBaseY - 18 * scale} ${width/2 + 10 * scale},${hairBaseY + 10 * scale}`} fill={hairColor} />
          <polygon points={`${width/2 + 10 * scale},${hairBaseY + 10 * scale} ${width/2 + 15 * scale},${hairBaseY - 12 * scale} ${width/2 + 20 * scale},${hairBaseY + 10 * scale}`} fill={hairColor} />
          <ellipse cx={width/2} cy={hairBaseY + 8 * scale} rx={24 * scale} ry={10 * scale} fill={hairColor} />
        </>
      ),
      mohawk: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 8 * scale} rx={18 * scale} ry={8 * scale} fill={hairColor} />
          <rect x={width/2 - 6 * scale} y={hairBaseY - 20 * scale} width={12 * scale} height={28 * scale} rx={4 * scale} fill={hairColor} />
        </>
      ),
      braids: (
        <>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={12 * scale} fill={hairColor} />
          <rect x={width/2 - 28 * scale} y={hairBaseY + 10 * scale} width={8 * scale} height={35 * scale} rx={3 * scale} fill={hairColor} />
          <rect x={width/2 + 20 * scale} y={hairBaseY + 10 * scale} width={8 * scale} height={35 * scale} rx={3 * scale} fill={hairColor} />
        </>
      ),
      buzzcut: (
        <ellipse cx={width/2} cy={hairBaseY + 8 * scale} rx={22 * scale} ry={10 * scale} fill={hairColor} opacity={0.7} />
      ),
      // ============ STORE UNLOCKABLE HAIRSTYLES ============
      galaxy_swirl: (
        <>
          {/* Cosmic spiral hair with sparkle effects */}
          <defs>
            <linearGradient id="galaxyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={26 * scale} ry={16 * scale} fill="url(#galaxyGradient)" />
          <ellipse cx={width/2 - 18 * scale} cy={hairBaseY + 22 * scale} rx={10 * scale} ry={18 * scale} fill="url(#galaxyGradient)" />
          <ellipse cx={width/2 + 18 * scale} cy={hairBaseY + 22 * scale} rx={10 * scale} ry={18 * scale} fill="url(#galaxyGradient)" />
          {/* Sparkles */}
          <circle cx={width/2 - 10 * scale} cy={hairBaseY - 2 * scale} r={2 * scale} fill="#FBBF24" opacity={0.9} />
          <circle cx={width/2 + 15 * scale} cy={hairBaseY + 5 * scale} r={1.5 * scale} fill="#FCD34D" opacity={0.8} />
          <circle cx={width/2 - 5 * scale} cy={hairBaseY + 10 * scale} r={1 * scale} fill="#FBBF24" opacity={0.7} />
        </>
      ),
      rainbow_streak: (
        <>
          {/* Rainbow flowing hair */}
          <defs>
            <linearGradient id="rainbowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="20%" stopColor="#F97316" />
              <stop offset="40%" stopColor="#FBBF24" />
              <stop offset="60%" stopColor="#22C55E" />
              <stop offset="80%" stopColor="#06B6D4" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={26 * scale} ry={15 * scale} fill="url(#rainbowGradient)" />
          <ellipse cx={width/2 - 20 * scale} cy={hairBaseY + 28 * scale} rx={10 * scale} ry={24 * scale} fill="url(#rainbowGradient)" />
          <ellipse cx={width/2 + 20 * scale} cy={hairBaseY + 28 * scale} rx={10 * scale} ry={24 * scale} fill="url(#rainbowGradient)" />
        </>
      ),
      crystal_crown: (
        <>
          {/* Elegant braided crown with crystals */}
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill={hairColor} />
          {/* Braided crown */}
          <ellipse cx={width/2} cy={hairBaseY - 2 * scale} rx={20 * scale} ry={6 * scale} fill={hairColor} stroke="#67E8F9" strokeWidth={2} />
          <ellipse cx={width/2 - 18 * scale} cy={hairBaseY + 20 * scale} rx={8 * scale} ry={15 * scale} fill={hairColor} />
          <ellipse cx={width/2 + 18 * scale} cy={hairBaseY + 20 * scale} rx={8 * scale} ry={15 * scale} fill={hairColor} />
          {/* Crystals */}
          <polygon points={`${width/2 - 12 * scale},${hairBaseY - 5 * scale} ${width/2 - 8 * scale},${hairBaseY - 12 * scale} ${width/2 - 4 * scale},${hairBaseY - 5 * scale}`} fill="#67E8F9" />
          <polygon points={`${width/2 + 4 * scale},${hairBaseY - 5 * scale} ${width/2 + 8 * scale},${hairBaseY - 14 * scale} ${width/2 + 12 * scale},${hairBaseY - 5 * scale}`} fill="#A5F3FC" />
        </>
      ),
      flame_tips: (
        <>
          {/* Hair with glowing ember tips */}
          <defs>
            <linearGradient id="flameGradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={hairColor} />
              <stop offset="60%" stopColor={hairColor} />
              <stop offset="80%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#FBBF24" />
            </linearGradient>
          </defs>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill="url(#flameGradient)" />
          <ellipse cx={width/2 - 18 * scale} cy={hairBaseY + 22 * scale} rx={9 * scale} ry={18 * scale} fill="url(#flameGradient)" />
          <ellipse cx={width/2 + 18 * scale} cy={hairBaseY + 22 * scale} rx={9 * scale} ry={18 * scale} fill="url(#flameGradient)" />
          {/* Ember glow */}
          <circle cx={width/2 - 15 * scale} cy={hairBaseY - 3 * scale} r={3 * scale} fill="#FBBF24" opacity={0.6} />
          <circle cx={width/2 + 12 * scale} cy={hairBaseY - 2 * scale} r={2 * scale} fill="#F97316" opacity={0.5} />
        </>
      ),
      ocean_waves: (
        <>
          {/* Flowing wavy hair like ocean waves */}
          <defs>
            <linearGradient id="oceanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06B6D4" />
              <stop offset="50%" stopColor="#0891B2" />
              <stop offset="100%" stopColor="#0E7490" />
            </linearGradient>
          </defs>
          <ellipse cx={width/2} cy={hairBaseY + 5 * scale} rx={24 * scale} ry={14 * scale} fill="url(#oceanGradient)" />
          {/* Wavy side sections */}
          <path 
            d={`M ${width/2 - 24 * scale} ${hairBaseY + 10 * scale} 
                Q ${width/2 - 28 * scale} ${hairBaseY + 20 * scale} ${width/2 - 22 * scale} ${hairBaseY + 30 * scale}
                Q ${width/2 - 16 * scale} ${hairBaseY + 40 * scale} ${width/2 - 20 * scale} ${hairBaseY + 50 * scale}
                L ${width/2 - 10 * scale} ${hairBaseY + 48 * scale}
                Q ${width/2 - 12 * scale} ${hairBaseY + 35 * scale} ${width/2 - 16 * scale} ${hairBaseY + 25 * scale}
                Q ${width/2 - 20 * scale} ${hairBaseY + 15 * scale} ${width/2 - 18 * scale} ${hairBaseY + 10 * scale} Z`}
            fill="url(#oceanGradient)"
          />
          <path 
            d={`M ${width/2 + 24 * scale} ${hairBaseY + 10 * scale} 
                Q ${width/2 + 28 * scale} ${hairBaseY + 20 * scale} ${width/2 + 22 * scale} ${hairBaseY + 30 * scale}
                Q ${width/2 + 16 * scale} ${hairBaseY + 40 * scale} ${width/2 + 20 * scale} ${hairBaseY + 50 * scale}
                L ${width/2 + 10 * scale} ${hairBaseY + 48 * scale}
                Q ${width/2 + 12 * scale} ${hairBaseY + 35 * scale} ${width/2 + 16 * scale} ${hairBaseY + 25 * scale}
                Q ${width/2 + 20 * scale} ${hairBaseY + 15 * scale} ${width/2 + 18 * scale} ${hairBaseY + 10 * scale} Z`}
            fill="url(#oceanGradient)"
          />
          {/* Wave shimmer */}
          <circle cx={width/2 + 8 * scale} cy={hairBaseY + 2 * scale} r={2 * scale} fill="#A5F3FC" opacity={0.6} />
        </>
      )
    };
    
    return hairStyles[hairStyle] || hairStyles.medium;
  };
  
  // Render face features (freckles, blush, etc.)
  const renderFaceFeatures = () => {
    const faceY = 28 * scale;
    
    if (faceFeature === 'freckles') {
      return (
        <>
          <circle cx={width/2 - 12 * scale} cy={faceY + 5 * scale} r={1.5 * scale} fill="#A67B5B" opacity={0.5} />
          <circle cx={width/2 - 8 * scale} cy={faceY + 8 * scale} r={1 * scale} fill="#A67B5B" opacity={0.5} />
          <circle cx={width/2 + 12 * scale} cy={faceY + 5 * scale} r={1.5 * scale} fill="#A67B5B" opacity={0.5} />
          <circle cx={width/2 + 8 * scale} cy={faceY + 8 * scale} r={1 * scale} fill="#A67B5B" opacity={0.5} />
        </>
      );
    }
    if (faceFeature === 'blush') {
      return (
        <>
          <ellipse cx={width/2 - 14 * scale} cy={faceY + 8 * scale} rx={6 * scale} ry={4 * scale} fill="#FDA4AF" opacity={0.4} />
          <ellipse cx={width/2 + 14 * scale} cy={faceY + 8 * scale} rx={6 * scale} ry={4 * scale} fill="#FDA4AF" opacity={0.4} />
        </>
      );
    }
    if (faceFeature === 'star_marks') {
      return (
        <text x={width/2 - 16 * scale} y={faceY + 3 * scale} fontSize={8 * scale} opacity={0.6}>⭐</text>
      );
    }
    return null;
  };
  
  // Render eyes
  const renderEyes = () => {
    const eyeY = 28 * scale;
    const eyeSpacing = 10 * scale;
    const eyeSize = 4 * scale;
    
    return (
      <>
        {/* Left eye */}
        <ellipse 
          cx={width/2 - eyeSpacing} 
          cy={eyeY} 
          rx={eyeSize} 
          ry={eyeShape === 'wide' ? eyeSize * 0.8 : eyeSize}
          fill={eyeColor}
        />
        <circle cx={width/2 - eyeSpacing - 1 * scale} cy={eyeY - 1 * scale} r={1.5 * scale} fill="white" opacity={0.6} />
        
        {/* Right eye */}
        <ellipse 
          cx={width/2 + eyeSpacing} 
          cy={eyeY} 
          rx={eyeSize} 
          ry={eyeShape === 'wide' ? eyeSize * 0.8 : eyeSize}
          fill={eyeColor}
        />
        <circle cx={width/2 + eyeSpacing - 1 * scale} cy={eyeY - 1 * scale} r={1.5 * scale} fill="white" opacity={0.6} />
      </>
    );
  };
  
  // Render smile
  const renderSmile = () => {
    const smileY = 38 * scale;
    const smileWidth = smileType === 'big_grin' ? 14 * scale : smileType === 'gentle' ? 8 * scale : 10 * scale;
    const smileHeight = smileType === 'excited' ? 5 * scale : 3 * scale;
    
    return (
      <path 
        d={`M ${width/2 - smileWidth/2} ${smileY} Q ${width/2} ${smileY + smileHeight * 2} ${width/2 + smileWidth/2} ${smileY}`}
        fill="none"
        stroke="#78350F"
        strokeWidth={2 * scale}
        strokeLinecap="round"
      />
    );
  };
  
  // Render accessory
  const renderAccessory = () => {
    const emoji = getAccessoryEmoji(accessory);
    if (!emoji) return null;
    
    // Position based on accessory type
    const headAccessories = ['crown', 'tiara', 'wizard_hat', 'beanie', 'cap', 'cat_ears', 'bunny_ears', 'antenna', 'halo', 'star_crown'];
    const faceAccessories = ['glasses', 'round_glasses', 'sport_glasses', 'goggles', 'magic_goggles'];
    const hairAccessories = ['hairclips', 'flower', 'feather', 'bow', 'butterfly_clips'];
    
    let posX = width/2;
    let posY = 0;
    let fontSize = 16 * scale;
    
    if (headAccessories.includes(accessory)) {
      posY = 2 * scale;
      fontSize = 18 * scale;
    } else if (faceAccessories.includes(accessory)) {
      posY = 26 * scale;
      fontSize = 14 * scale;
    } else if (hairAccessories.includes(accessory)) {
      posX = width/2 + 15 * scale;
      posY = 12 * scale;
      fontSize = 12 * scale;
    } else {
      // Neck accessories
      posY = 52 * scale;
      fontSize = 12 * scale;
    }
    
    return (
      <text 
        x={posX} 
        y={posY} 
        fontSize={fontSize} 
        textAnchor="middle" 
        dominantBaseline="middle"
      >
        {emoji}
      </text>
    );
  };
  
  return (
    <div 
      className={`relative ${enableBreathing ? 'animate-avatar-breathe' : ''}`}
      style={{ width, height }}
    >
      {/* Aura effect */}
      {effectiveShowAura && (
        <div 
          className="absolute -inset-2 rounded-full animate-pulse-slow"
          style={{ 
            background: `radial-gradient(circle, ${effectiveAuraColor}40 0%, transparent 70%)`,
            filter: 'blur(8px)'
          }}
        />
      )}
      
      {/* Sparkles */}
      {effectiveShowSparkles && (
        <div className="absolute -inset-4 pointer-events-none overflow-visible">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-sparkle"
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animationDelay: `${i * 0.15}s`,
                width: '4px',
                height: '4px',
                backgroundColor: ['#FBBF24', '#F59E0B', '#FCD34D'][i % 3]
              }}
            />
          ))}
        </div>
      )}
      
      {/* Main SVG Avatar */}
      <svg 
        width={width} 
        height={height} 
        viewBox={`0 0 ${width} ${height}`}
        className="relative z-10"
      >
        {/* LAYER 1: BODY */}
        {/* Legs */}
        <rect 
          x={width/2 - 18 * scale} 
          y={height - 50 * scale} 
          width={12 * scale} 
          height={45 * scale} 
          rx={5 * scale}
          fill={outfitColorDark}
        />
        <rect 
          x={width/2 + 6 * scale} 
          y={height - 50 * scale} 
          width={12 * scale} 
          height={45 * scale} 
          rx={5 * scale}
          fill={outfitColorDark}
        />
        
        {/* Shoes */}
        <ellipse 
          cx={width/2 - 12 * scale} 
          cy={height - 4 * scale} 
          rx={10 * scale} 
          ry={5 * scale}
          fill="#1F2937"
        />
        <ellipse 
          cx={width/2 + 12 * scale} 
          cy={height - 4 * scale} 
          rx={10 * scale} 
          ry={5 * scale}
          fill="#1F2937"
        />
        
        {/* LAYER 2: OUTFIT (torso, arms) - uses special rendering for store outfits */}
        {renderOutfit()}
        
        {/* Hands */}
        <circle cx={width/2 - 32 * scale} cy={height - 50 * scale} r={6 * scale} fill={skinColor} />
        <circle cx={width/2 + 32 * scale} cy={height - 50 * scale} r={6 * scale} fill={skinColor} />
        
        {/* Neck */}
        <rect 
          x={width/2 - 6 * scale} 
          y={height - 110 * scale} 
          width={12 * scale} 
          height={12 * scale}
          fill={skinColor}
        />
        
        {/* LAYER 2: HEAD */}
        {/* Head base */}
        <ellipse 
          cx={width/2} 
          cy={25 * scale} 
          rx={24 * scale} 
          ry={26 * scale}
          fill={skinColor}
        />
        
        {/* LAYER 3: HAIR (behind head for some styles) */}
        {renderHair()}
        
        {/* LAYER 4: FACE */}
        {renderFaceFeatures()}
        {renderEyes()}
        {renderSmile()}
        
        {/* LAYER 5: ACCESSORIES */}
        {renderAccessory()}
      </svg>
      
      {/* Type badge */}
      {showBadge && (
        <div 
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[8px] font-bold whitespace-nowrap z-20"
          style={{ backgroundColor: type === 'explorer' ? '#818CF8' : '#FBBF24', color: 'white' }}
        >
          {type === 'explorer' ? 'Explorer' : 'Guide'}
        </div>
      )}
    </div>
  );
};

// ============ LEGACY FACE-ONLY AVATAR (for backwards compatibility) ============
// Keep the original AvatarPreview for places that need the compact face view
export const AvatarPreview = ({ 
  avatar, 
  size = 'md', 
  showBadge = false, 
  type = 'explorer', 
  showAura = false, 
  auraColor = '#818CF8', 
  showBreathing = false, 
  showSparkles = false,
  showBlinking = false,
  showCelebration = false,
  isIdle = false
}) => {
  const sizes = {
    sm: { container: 'w-8 h-8', eyes: 'w-1 h-1', eyeGap: 'gap-1', smile: 'w-1.5', mt: 'mt-1', accSize: 'text-[10px]', accPos: 'top-0 -right-1' },
    md: { container: 'w-12 h-12', eyes: 'w-1 h-1', eyeGap: 'gap-1.5', smile: 'w-2', mt: 'mt-1.5', accSize: 'text-sm', accPos: '-top-1 -right-1' },
    lg: { container: 'w-24 h-24', eyes: 'w-2 h-2', eyeGap: 'gap-3', smile: 'w-4', mt: 'mt-3', accSize: 'text-lg', accPos: '-top-2 -right-2' },
    xl: { container: 'w-32 h-32', eyes: 'w-2.5 h-2.5', eyeGap: 'gap-4', smile: 'w-5', mt: 'mt-4', accSize: 'text-xl', accPos: '-top-2 -right-2' }
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  const enableBreathing = showBreathing || isIdle;
  const enableBlinking = showBlinking || isIdle;
  
  const outfitColors = {
    explorer_tunic: '#818CF8', star_robe: '#FBBF24', forest_cloak: '#22C55E',
    ocean_vest: '#06B6D4', fire_jacket: '#EF4444', sky_cape: '#8B5CF6'
  };
  
  const outfitColor = outfitColors[avatar?.outfit] || '#818CF8';
  const hairColor = avatar?.hair_color || '#2C1810';
  const skinColor = avatar?.skin_tone || '#F5D0C5';
  const hairStyle = avatar?.hair_style || 'medium';
  
  const hairHeights = {
    short: '20%', medium: '28%', long: '38%', curly: '30%',
    wavy: '32%', coily: '28%', afro: '35%', locs: '30%',
    twists: '28%', mohawk: '25%', buzzcut: '15%', fade: '18%',
    spiky: '22%', ponytail: '26%', braids: '28%', buns: '20%',
    pigtails: '24%', cornrows: '22%', straight: '30%'
  };
  
  const getAccessoryEmoji = (accessory) => {
    const emojis = {
      none: '', glasses: '👓', round_glasses: '🤓', sport_glasses: '🕶️',
      crown: '👑', tiara: '👸', wizard_hat: '🧙', beanie: '🧢',
      headband: '🎗️', cat_ears: '🐱', bunny_ears: '🐰',
      headphones: '🎧', wings: '🦋', halo: '😇',
      hairclips: '🎀', flower: '🌸'
    };
    return emojis[accessory] || '';
  };
  
  return (
    <div className={`relative ${sizeConfig.container} ${showCelebration ? 'animate-celebration-wiggle' : ''}`}>
      {showAura && (
        <div 
          className="absolute -inset-2 rounded-full animate-pulse-slow opacity-50"
          style={{ 
            background: `radial-gradient(circle, ${auraColor}40 0%, transparent 70%)`,
            filter: 'blur(8px)'
          }}
        />
      )}
      
      {(showSparkles || showCelebration) && (
        <div className="absolute -inset-4 pointer-events-none overflow-visible">
          {[...Array(showCelebration ? 10 : 6)].map((_, i) => (
            <div
              key={i}
              className={`absolute rounded-full ${showCelebration ? 'animate-celebration-float' : 'animate-sparkle'}`}
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animationDelay: `${i * 0.15}s`,
                width: showCelebration ? '6px' : '4px',
                height: showCelebration ? '6px' : '4px',
                backgroundColor: ['#FBBF24', '#F59E0B', '#FCD34D'][i % 3]
              }}
            />
          ))}
        </div>
      )}
      
      <div className={`absolute inset-0 rounded-full overflow-hidden ${enableBreathing ? 'animate-avatar-breathe' : ''}`}>
        <div 
          className="absolute inset-0 rounded-full"
          style={{ 
            background: `linear-gradient(135deg, ${outfitColor}40 0%, ${outfitColor}20 100%)`,
            border: `2px solid ${outfitColor}60`
          }}
        />
        <div className="absolute inset-[15%] rounded-full" style={{ backgroundColor: skinColor }} />
        <div 
          className="absolute top-0 left-0 right-0 rounded-t-full"
          style={{ backgroundColor: hairColor, height: hairHeights[hairStyle] || '28%' }}
        />
      </div>
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`relative ${sizeConfig.mt}`}>
          <div className={`flex ${sizeConfig.eyeGap} mb-1 ${enableBlinking ? 'animate-avatar-blink' : ''}`}>
            <div className={`${sizeConfig.eyes} rounded-full`} style={{ backgroundColor: avatar?.eye_color || '#4A3728' }}>
              <div className="absolute bg-white rounded-full opacity-60" style={{ top: '15%', left: '15%', width: '30%', height: '30%' }} />
            </div>
            <div className={`${sizeConfig.eyes} rounded-full`} style={{ backgroundColor: avatar?.eye_color || '#4A3728' }}>
              <div className="absolute bg-white rounded-full opacity-60" style={{ top: '15%', left: '15%', width: '30%', height: '30%' }} />
            </div>
          </div>
          <div className={`${sizeConfig.smile} mx-auto`} style={{ borderRadius: '0 0 8px 8px', height: '3px', backgroundColor: '#78350f' }} />
        </div>
      </div>
      
      {avatar?.accessory && avatar.accessory !== 'none' && (
        <div className={`absolute ${sizeConfig.accPos} ${sizeConfig.accSize}`}>
          {getAccessoryEmoji(avatar.accessory)}
        </div>
      )}
      
      {showBadge && (
        <div 
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 py-0.5 rounded text-[8px] font-bold whitespace-nowrap z-10"
          style={{ backgroundColor: type === 'explorer' ? '#818CF8' : '#FBBF24', color: 'white' }}
        >
          {type === 'explorer' ? 'Explorer' : 'Guide'}
        </div>
      )}
    </div>
  );
};

// ============ AVATAR BUILDER MODAL ============
export const AvatarBuilderModal = ({ 
  isOpen, 
  onClose, 
  childId = null,
  initialAvatar = null,
  onSave,
  useFullBody = true // New prop to toggle between full-body and face view
}) => {
  const [avatar, setAvatar] = useState(initialAvatar || {
    body_type: 'rounded',
    skin_tone: '#F5D0C5',
    hair_style: 'short',
    hair_color: '#2C1810',
    eye_shape: 'round',
    eye_color: '#4A3728',
    face_feature: 'none',
    smile: 'happy',
    outfit: 'explorer_tunic',
    accessory: 'none'
  });
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('skin_tone');
  
  const isExplorer = childId !== null;
  
  useEffect(() => {
    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);
  
  const loadOptions = async () => {
    try {
      const res = await avatarAPI.getOptions();
      setOptions(res.data);
      if (initialAvatar) {
        setAvatar(initialAvatar);
      } else if (res.data.default_avatar) {
        setAvatar(res.data.default_avatar);
      }
    } catch (error) {
      console.error('Error loading avatar options:', error);
      toast.error('Could not load avatar options');
    } finally {
      setLoading(false);
    }
  };
  
  const randomizeAvatar = () => {
    if (!options?.options) return;
    
    const randomChoice = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const opts = options.options;
    
    setAvatar({
      body_type: randomChoice(opts.body_type || ['rounded']),
      skin_tone: randomChoice(opts.skin_tone || ['#F5D0C5']),
      hair_style: randomChoice(opts.hair_style || ['short']),
      hair_color: randomChoice(opts.hair_color || ['#2C1810']),
      eye_shape: randomChoice(opts.eye_shape || ['round']),
      eye_color: randomChoice(opts.eye_color || ['#4A3728']),
      face_feature: randomChoice(opts.face_feature || ['none']),
      smile: randomChoice(opts.smile || ['happy']),
      outfit: randomChoice(opts.outfit || ['explorer_tunic']),
      accessory: randomChoice(opts.accessory || ['none'])
    });
    
    toast.success('Random explorer created!');
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      if (isExplorer) {
        await avatarAPI.updateChildAvatar(childId, avatar);
      } else {
        await avatarAPI.updateParentAvatar(avatar);
      }
      toast.success('Avatar saved!');
      onSave?.(avatar);
      onClose();
    } catch (error) {
      toast.error('Could not save avatar');
    } finally {
      setSaving(false);
    }
  };
  
  const updateAvatar = (field, value) => {
    setAvatar(prev => ({ ...prev, [field]: value }));
  };
  
  const categories = [
    { id: 'skin_tone', name: 'Skin', icon: User },
    { id: 'hair_style', name: 'Hair', icon: Sparkles },
    { id: 'hair_color', name: 'Color', icon: Palette },
    { id: 'eye_shape', name: 'Eyes', icon: Eye },
    { id: 'eye_color', name: 'Eye Color', icon: Palette },
    { id: 'face_feature', name: 'Face', icon: Smile },
    { id: 'smile', name: 'Smile', icon: Smile },
    { id: 'outfit', name: 'Outfit', icon: Shirt },
    { id: 'accessory', name: 'Accessory', icon: Crown }
  ];
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="glass-panel p-6 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-philosopher font-bold text-white">
              Create Your {isExplorer ? 'Temple Explorer' : 'Temple Guide'}
            </h2>
            <p className="text-sm text-slate-400">
              {isExplorer ? 'Design your full-body avatar!' : 'Customize your guide appearance'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-indigo-500 rounded-full mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Preview - Full Body or Face */}
            <div className="flex justify-center mb-4">
              <div className="relative bg-gradient-to-b from-slate-800/50 to-slate-900/50 rounded-2xl p-6">
                {useFullBody ? (
                  <FullBodyAvatarPreview 
                    avatar={avatar} 
                    size="xl" 
                    showBadge 
                    type={isExplorer ? 'explorer' : 'guide'}
                    isIdle
                  />
                ) : (
                  <AvatarPreview 
                    avatar={avatar} 
                    size="xl" 
                    showBadge 
                    type={isExplorer ? 'explorer' : 'guide'}
                    isIdle
                  />
                )}
              </div>
            </div>
            
            {/* Randomize Button */}
            <div className="flex justify-center mb-6">
              <button
                onClick={randomizeAvatar}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/30 rounded-full text-purple-300 transition-all"
                data-testid="randomize-avatar-btn"
              >
                <Shuffle className="w-4 h-4" />
                Random Explorer
              </button>
            </div>
            
            {/* Category tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                      activeCategory === cat.id
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'bg-slate-900/50 text-slate-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{cat.name}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Options grid */}
            <div className="bg-slate-900/50 p-4 rounded-xl mb-6 max-h-48 overflow-y-auto">
              {activeCategory === 'skin_tone' && options?.options?.skin_tone && (
                <div className="grid grid-cols-8 gap-2">
                  {options.options.skin_tone.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('skin_tone', color)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        avatar.skin_tone === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {activeCategory === 'hair_style' && options?.options?.hair_style && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {options.options.hair_style.map(style => (
                    <button
                      key={style}
                      onClick={() => updateAvatar('hair_style', style)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        avatar.hair_style === style 
                          ? 'bg-indigo-500/20 border border-indigo-500/30 text-white' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-xs capitalize">{style.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'hair_color' && options?.options?.hair_color && (
                <div className="grid grid-cols-10 gap-2">
                  {options.options.hair_color.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('hair_color', color)}
                      className={`w-6 h-6 rounded-full transition-all ${
                        avatar.hair_color === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {activeCategory === 'eye_shape' && options?.options?.eye_shape && (
                <div className="grid grid-cols-4 gap-2">
                  {options.options.eye_shape.map(shape => (
                    <button
                      key={shape}
                      onClick={() => updateAvatar('eye_shape', shape)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        avatar.eye_shape === shape 
                          ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <Eye className="w-4 h-4 mx-auto mb-1" />
                      <span className="text-xs capitalize">{shape}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'eye_color' && options?.options?.eye_color && (
                <div className="grid grid-cols-8 gap-2">
                  {options.options.eye_color.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('eye_color', color)}
                      className={`w-8 h-8 rounded-full transition-all border-2 ${
                        avatar.eye_color === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105 border-slate-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {activeCategory === 'face_feature' && options?.options?.face_feature && (
                <div className="grid grid-cols-4 gap-2">
                  {options.options.face_feature.map(feature => (
                    <button
                      key={feature}
                      onClick={() => updateAvatar('face_feature', feature)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        avatar.face_feature === feature 
                          ? 'bg-pink-500/20 border border-pink-500/30 text-pink-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-xs capitalize">{feature.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'smile' && options?.options?.smile && (
                <div className="grid grid-cols-4 gap-2">
                  {options.options.smile.map(smileType => (
                    <button
                      key={smileType}
                      onClick={() => updateAvatar('smile', smileType)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        avatar.smile === smileType 
                          ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <Smile className="w-4 h-4 mx-auto mb-1" />
                      <span className="text-xs capitalize">{smileType.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'outfit' && options?.options?.outfit && (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {options.options.outfit.map(outfitName => {
                    const outfitColors = {
                      explorer_tunic: '#818CF8', star_robe: '#FBBF24', forest_cloak: '#22C55E',
                      ocean_vest: '#06B6D4', fire_jacket: '#EF4444', sky_cape: '#8B5CF6',
                      moon_robes: '#94A3B8', sun_garment: '#F59E0B', crystal_armor: '#67E8F9',
                      hoodie: '#64748B', cozy_sweater: '#FB7185', t_shirt: '#38BDF8',
                      explorer_jacket: '#A16207', adventurer_vest: '#65A30D', ranger_cloak: '#166534',
                      royal_cape: '#7C3AED', wizard_robe: '#4338CA', enchanted_dress: '#EC4899',
                      superhero_suit: '#DC2626', space_suit: '#3730A3', ninja_outfit: '#1F2937',
                      pirate_vest: '#92400E'
                    };
                    const outfitColor = outfitColors[outfitName] || '#818CF8';
                    return (
                      <button
                        key={outfitName}
                        onClick={() => updateAvatar('outfit', outfitName)}
                        className={`p-2 rounded-lg text-center transition-all ${
                          avatar.outfit === outfitName 
                            ? 'border-2 scale-105' 
                            : 'bg-slate-800/50 hover:bg-slate-700/50'
                        }`}
                        style={avatar.outfit === outfitName ? { 
                          borderColor: outfitColor,
                          backgroundColor: `${outfitColor}20`
                        } : {}}
                      >
                        <div 
                          className="w-6 h-6 rounded mx-auto mb-1"
                          style={{ backgroundColor: outfitColor }}
                        />
                        <span className="text-[10px] text-slate-300 capitalize leading-tight">
                          {outfitName.replace('_', ' ')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {activeCategory === 'accessory' && options?.options?.accessory && (
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {options.options.accessory.map(acc => (
                    <button
                      key={acc}
                      onClick={() => updateAvatar('accessory', acc)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        avatar.accessory === acc 
                          ? 'bg-indigo-500/20 border border-indigo-500/30 text-white' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-xs capitalize">{acc.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin w-5 h-5 border-2 border-indigo-300/20 border-t-indigo-300 rounded-full"></div>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Save Avatar</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AvatarBuilderModal;
